import { defineStore } from 'pinia'
import { ref } from 'vue'

export type StepState = 'done' | 'active' | 'todo'

/**
 * The five steps of the calculation animation. The labels land in the checklist; the percentage is
 * where the bar crawls to while that step runs. Every step takes one to two seconds, so each point
 * can actually be read (#6331); they still vary within that band, because identical ticks read as
 * a fake loop, and comparing the two situations is the heavy lifting of this calculation. 66 at
 * the start of step three matches the design still; the exact run is still a design question
 * (#6313).
 */
export const CALCULATION_STEPS = [
  { label: 'Verbruik analyseren', duration: 1300, percentage: 50 },
  { label: 'Teruglevering berekenen', duration: 1500, percentage: 66 },
  { label: 'Situatie vóór en na 2027 vergelijken', duration: 1900, percentage: 83 },
  { label: 'Impact bepalen', duration: 1500, percentage: 92 },
  { label: 'Persoonlijk actieplan maken', duration: 1300, percentage: 100 },
]

/**
 * The rest after the last check lands: the visitor sees the finished list before the result may
 * take its place, also when the answer arrived long ago.
 */
export const SETTLE_MS = 1000

/** Every run breathes differently: each step gets up to this much slack, either way, staying
    within the one-to-two-second band per step. */
export const STEP_JITTER_MS = 200

/** Where the bar picks up from: the input step left it at 33. */
export const START_PERCENTAGE = 33

/** The first bar target lands one tick later, so the bar visibly travels instead of starting there. */
export const BAR_KICKOFF_MS = 50

/**
 * Drives the calculation animation. The steps have fixed timing and say nothing about the one
 * request that runs meanwhile; the flow in App.vue combines the two. A store, because the
 * checklist, the progress bar and the flow all read the same run.
 */
export const useAnimationStore = defineStore('animation', () => {
  const playing = ref(false)
  const finished = ref(false)
  const completedSteps = ref(0)

  /**
   * The bar's current target and how long it may take to get there: the CSS transition eases
   * towards the target over the running step, so the bar never sits still mid-run.
   */
  const percentage = ref(0)
  const barDuration = ref(0)

  let timers: ReturnType<typeof setTimeout>[] = []

  function stepState(index: number): StepState {
    if (index < completedSteps.value) {
      return 'done'
    }

    return index === completedSteps.value && playing.value ? 'active' : 'todo'
  }

  function play(): void {
    stop()
    finished.value = false
    playing.value = true

    // Not an animation person: show the state of affairs directly.
    if (prefersReducedMotion()) {
      completedSteps.value = CALCULATION_STEPS.length
      percentage.value = 100
      barDuration.value = 0
      finish()

      return
    }

    // A fresh jitter per run, so a rerun never plays the exact same rhythm.
    const run = CALCULATION_STEPS.map((step) => ({ ...step, duration: jitter(step.duration) }))

    completedSteps.value = 0
    percentage.value = START_PERCENTAGE
    // Minus the kickoff, so the first transition still arrives exactly on the step boundary.
    barDuration.value = run[0].duration - BAR_KICKOFF_MS
    schedule(() => (percentage.value = run[0].percentage), BAR_KICKOFF_MS)

    let elapsed = 0

    run.forEach((step, index) => {
      elapsed += step.duration
      const next = run[index + 1]

      schedule(() => {
        completedSteps.value = index + 1

        if (next) {
          barDuration.value = next.duration
          percentage.value = next.percentage
        }
      }, elapsed)
    })

    schedule(finish, elapsed + SETTLE_MS)
  }

  /** Freezes the run where it is; the failure message takes the stage. */
  function stop(): void {
    timers.forEach(clearTimeout)
    timers = []
    playing.value = false
  }

  /** Back to square one, for a fresh visit of the input step. */
  function reset(): void {
    stop()
    finished.value = false
    completedSteps.value = 0
    percentage.value = 0
    barDuration.value = 0
  }

  function finish(): void {
    playing.value = false
    finished.value = true
  }

  function schedule(callback: () => void, ms: number): void {
    timers.push(setTimeout(callback, ms))
  }

  return { playing, finished, completedSteps, percentage, barDuration, stepState, play, stop, reset }
})

export function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function jitter(duration: number): number {
  return Math.round(duration + (Math.random() - 0.5) * 2 * STEP_JITTER_MS)
}
